import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter, propTypes as routerPropTypes } from 'react-router';
import { FormattedDate, FormattedMessage, injectIntl } from 'react-intl';
import moment from 'moment';
import classNames from 'classnames';
import Clipboard from 'clipboard';
import capitalize from 'lodash.capitalize';
import { CSSTransitionGroup } from 'react-transition-group';
import UserIcon from 'react-material-icons/icons/social/person';
import PlaceIcon from 'react-material-icons/icons/maps/place';
import CancelIcon from 'react-material-icons/icons/navigation/cancel';
import WarningIcon from 'react-material-icons/icons/alert/warning';
import StoreIcon from 'react-material-icons/icons/action/store';
import GiftIcon from 'react-material-icons/icons/action/card-giftcard';
import CSIcon from '@chama/ui-components/components/Icons/cs-icon';
import CheckIcon from 'react-material-icons/icons/action/check-circle';
import CopyIcon from 'react-material-icons/icons/content/content-copy';
import AppInsights from '@app/appInsights';
import config from '@app/config';
import phoneFormatters from '@chama/utils/i18n/format-phone';
import cancellationReason from '@chama/utils/labels/cancellation-reasons';
import deliveryStatuses from '@chama/utils/labels/delivery-status';
import { formatPrice } from '@chama/utils/functions/currency';
import { getOrderTotal } from '@chama/utils/functions/order-values';
import {
  TYPE_NEW_ORDER,
  TYPE_DELIVER_ORDER,
  TYPE_CANCEL_NEW_ORDER,
  TYPE_CANCEL_DELIVER_ORDER,
  STATUS_DONE,
  ORDER_STATUS_DEALER_CANCELLED,
  ORDER_STATUS_DEALER_DECLINED,
  QUEUE_BEING_DELIVERED,
  QUEUE_INCOMING,
  QUEUE_FINALIZED,
} from '@chama/utils/constants/status';
import { CASH, CREDIT, DEBIT } from '@chama/utils/constants/payments';
import windowWatcher from '@chama/ui-components/hocs/window-watcher';
import MapThumbBtn from '@chama/ui-components/components/MapThumbBtn';
import Tooltip from '@chama/ui-components/components/Tooltip';
import Spinner from '@chama/ui-components/components/Spinner';
import Button from '@chama/ui-components/components/Button';
import Switch from '@chama/ui-components/components/Switch';
import Modal from '@chama/ui-components/components/Modal';
import OrderPriceOverview from '@chama/ui-components/components/OrderPriceOverview';
import IconTextInfo from '@chama/ui-components/components/IconTextInfo';
import eyeIcon from '@chama/statics/assets/svg/eye-blue.svg';
import TimeTextLine from '@chama/ui-components/components/TimeTextLine';
import ActionButtons from '@chama/ui-components/components/ActionButtons';
import EtaSwitch from '@chama/ui-components/components/EtaSwitch';
import Dealer from '@app/models/Dealer';
import MdtModals from './MdtModals';
import EventHistory from './EventHistory';

import './index.css';

const propTypes = {
  ...routerPropTypes,
  order: PropTypes.object,
  orderDetails: PropTypes.object,
  isLoadingCommand: PropTypes.bool.isRequired,
  displayDealerName: PropTypes.bool.isRequired,
  isFetchingEventHistory: PropTypes.bool.isRequired,
  isOrderBeingProcessed: PropTypes.bool.isRequired,
  getNearbyDealers: PropTypes.func.isRequired,
  fetchEventHistory: PropTypes.func.isRequired,
  nearbyDealersList: PropTypes.array.isRequired,
  eventHistory: PropTypes.array,
  isFetchingOrderDetails: PropTypes.bool.isRequired,
};

const defaultProps = {
  order: undefined,
  eventHistory: [],
  orderDetails: undefined,
};

const ETAS = [
  { value: '15', window: '15', label: 'até 15 min' },
  { value: '30', window: '15', label: '15 - 30 min' },
  { value: '45', window: '15', label: '30 - 45 min' },
  { value: '60', window: '15', label: '45 - 60 min' },
];

const formatPhone = phoneFormatters['pt-BR'];

class OrderDetailsLayout extends Component {
  constructor(props) {
    super(props);

    this.state = {
      eta: '60',
      etaWindow: '15',
      loadingNearbyDealers: false,
      isOrderDetailsOptionsOpen: false,
    };
  }

  componentDidMount() {
    const { windowHeight, isFixed } = this.props;
    this.componentHeight = this.component.offsetHeight;

    this.measureOffsetTop(isFixed);

    const offsetTop = this.getOffsetTop(isFixed);

    this.changeContentHeightIfNeeded(windowHeight, offsetTop);
  }

  componentWillReceiveProps(nextProps) {
    const { windowHeight, isFixed } = nextProps;
    const currentOrderTicket = this.props.order.currentTicket;
    const newOrderTicket = nextProps.order.currentTicket;
    const hasQueueChanged = currentOrderTicket.queue !== newOrderTicket.queue;
    const isSameOrder = this.props.order.id === nextProps.order.id;

    if (isFixed !== this.props.isFixed) {
      this.measureOffsetTop(isFixed);

      const offsetTop = this.getOffsetTop(isFixed);

      this.changeContentHeightIfNeeded(windowHeight, offsetTop);
    }

    if (windowHeight !== this.props.windowHeight) {
      const offsetTop = this.getOffsetTop(isFixed);
      this.changeContentHeightIfNeeded(windowHeight, offsetTop);
    }

    if (currentOrderTicket && newOrderTicket && hasQueueChanged && isSameOrder) {
      this.close();
    }
  }

  get ticket() {
    const { order } = this.props;

    return order && order.currentTicket;
  }

  get isCancelledByCustomer() {
    const { type } = this.ticket;

    return type === TYPE_CANCEL_NEW_ORDER || type === TYPE_CANCEL_DELIVER_ORDER;
  }

  get isCancelledByDealer() {
    const {
      order: { status },
    } = this.props;

    return status === ORDER_STATUS_DEALER_CANCELLED || status === ORDER_STATUS_DEALER_DECLINED;
  }

  get isCancelNewOrder() {
    return this.ticket.type === TYPE_CANCEL_NEW_ORDER;
  }

  get isDeliverOrder() {
    return this.ticket.type === TYPE_DELIVER_ORDER;
  }

  get isNewOrder() {
    return this.ticket.type === TYPE_NEW_ORDER;
  }

  measureOffsetTop = isFixed => {
    const { top } = this.component.getBoundingClientRect();

    if (isFixed && !this.fixedOffsetTop) {
      this.fixedOffsetTop = top;
    } else if (!this.normalOffsetTop) {
      this.normalOffsetTop = top;
    }
  };

  getOffsetTop = isFixed => {
    if (isFixed) {
      return this.fixedOffsetTop;
    }

    return this.normalOffsetTop;
  };

  changeContentHeightIfNeeded = (windowHeight, offsetTop) => {
    const spacing = 20;

    if (windowHeight - offsetTop <= this.componentHeight) {
      this.content.style.height = `${windowHeight - offsetTop - spacing}px`;
    } else {
      this.content.style.height = 'auto';
    }
  };

  handleRef = refName => {
    return ref => {
      this[refName] = ref;
    };
  };

  close = () => this.props.closeDetails();

  startDelivery = e => {
    e.preventDefault();
    this.track('mdt-order-accept-button-pressed');
    this.props.startDelivery(this.ticket.id, this.state.eta, this.state.etaWindow);
  };

  declineOrder = (reason, message) => {
    this.track('mdt-order-decline-button-pressed');
    this.props.declineOrder(this.ticket.id, reason, message);
  };

  deliverOrder = e => {
    e.preventDefault();
    this.track('mdt-order-deliver-button-pressed');
    this.props.deliverOrder(this.ticket.id);
  };

  cancelOrder = (reason, message) => {
    this.track('mdt-order-cancel-button-pressed');
    this.props.cancelOrder(this.ticket.id, reason, message);
  };

  dismissCancelNewOrder = e => {
    const { id, eTag } = this.ticket;

    e.preventDefault();

    this.props.dismissOrder(id, eTag);
  };

  dismissCancelDeliverOrder = e => {
    const { id, eTag } = this.ticket;

    e.preventDefault();

    this.props.cancelDelivery(id, eTag);
  };

  toggleTicketControl = () => {
    const { unAssignTicketToCurrentUser, isAssignedToCurrentUser } = this.props;
    const { id } = this.ticket;

    if (isAssignedToCurrentUser) {
      unAssignTicketToCurrentUser(id);
    } else {
      this.takeControlModal.show();
    }
  };

  displayText = (text, entity) => {
    const {
      intl: { formatMessage },
    } = this.props;

    if (!text || (text && text.length === 0)) {
      const i18nEntity = capitalize(
        formatMessage({
          id: entity,
          defaultMessage: entity,
        }),
      );

      const uninformedI18n = formatMessage({
        id: 'not_available',
        defaultMessage: 'not available',
      });

      return `${i18nEntity} ${uninformedI18n}`;
    }

    return text;
  };

  onETAChange = etaIndex => {
    const { value, window } = ETAS[etaIndex];

    this.setState({
      eta: value,
      etaWindow: window,
    });
  };

  openReallocationModal = () => {
    const { lat, lon } = this.props.order;
    const doneLoading = () => {
      this.reallocationModal.show();
      this.setState({ loadingNearbyDealers: false });
    };

    this.setState({ loadingNearbyDealers: true });

    this.props.getNearbyDealers(lat, lon).then(doneLoading);
  };

  renderConsumerDeliveredStatus = () => {
    const {
      order: { consumerDelivered },
    } = this.props;

    if (consumerDelivered) {
      return (
        <div className="OrderDetailsLayout__row">
          <div className="OrderDetailsLayout__consumer_delivered OrderDetailsLayout__col OrderDetailsLayout__col--centered">
            <IconTextInfo Icon={CheckIcon} iconFullOpacity>
              <strong>
                <FormattedMessage
                  id="order_details_consumer_delivered_status"
                  defaultMessage="This order was flagged as delivered by the consumer"
                />
              </strong>
            </IconTextInfo>
          </div>
        </div>
      );
    }

    return null;
  };

  renderActionTimeContent = () => {
    const {
      isAssignedToCurrentUser,
      order: { eta, etaWindowMinutes },
      isLoadingAssignment,
    } = this.props;
    const { status } = this.ticket;

    if (status === STATUS_DONE) {
      if (this.isCancelledByDealer || this.isCancelledByCustomer) {
        return (
          <div className="OrderDetailsLayout__text_line">
            <TimeTextLine time={this.ticket.modificationTime}>
              <FormattedMessage id="cancelled_at" defaultMessage="Cancelled at" />
            </TimeTextLine>
          </div>
        );
      }

      return (
        <div className="OrderDetailsLayout__text_line">
          <TimeTextLine time={this.ticket.modificationTime}>
            <FormattedMessage id="delivered_confirmed_at" defaultMessage="Delivery confirmed at" />
          </TimeTextLine>
        </div>
      );
    }

    if (this.isNewOrder) {
      const selectedEta = this.state.eta;

      return (
        isAssignedToCurrentUser && (
          <EtaSwitch
            etaList={ETAS}
            currentEta={selectedEta}
            enabled={isAssignedToCurrentUser || isLoadingAssignment}
            onEtaChange={this.onETAChange}
          />
        )
      );
    }

    if (this.isDeliverOrder) {
      if (etaWindowMinutes && etaWindowMinutes > 0) {
        const windowEta = moment(eta).subtract(etaWindowMinutes, 'minutes');

        return (
          <div className="OrderDetailsLayout__text_line">
            <div>
              <strong>
                <FormattedMessage
                  id="delivery_etasetto"
                  defaultMessage="Estimated time of arrival"
                />
              </strong>
            </div>
            <div>
              <FormattedDate value={new Date(eta)} year="numeric" month="long" day="2-digit" />
              &nbsp;
              <FormattedMessage id="between" defaultMessage="between" />
              &nbsp;
              {moment(windowEta).format('HH:mm')}
              &nbsp;
              <FormattedMessage id="and" defaultMessage="and" />
              &nbsp;
              {moment(eta).format('HH:mm')}
            </div>
          </div>
        );
      }

      return (
        <div className="OrderDetailsLayout__text_line">
          <TimeTextLine time={eta}>
            <FormattedMessage id="delivery_etasetto" defaultMessage="Estimated time of arrival" />
          </TimeTextLine>
        </div>
      );
    }

    return null;
  };

  renderHeader = () => {
    const { displayDealerName } = this.props;

    if (displayDealerName) {
      const { dealer } = this.props;

      return (
        <div className="OrderDetailsLayout__panel OrderDetailsLayout__panel--shop_name">
          <div className="OrderDetailsLayout__row">
            <div className="OrderDetailsLayout__col OrderDetailsLayout__col--centered">
              <div>
                <StoreIcon className="OrderDetailsLayout__icon" />
              </div>
              <div className="OrderDetailsLayout__text">
                <strong>{dealer.name}</strong>
                <a
                  href={`${config.newMdtHost}/revendedores/${Dealer.getMainShopIdFromSubshopId(
                    dealer.id,
                  )}/detalhes`}
                  className="OrderDetailsLayout__dealer-link"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <FormattedMessage id="order_history.see_details" />
                </a>
              </div>
            </div>
            <div className="OrderDetails__col OrderDetails__col--buttons">
              <Button
                variant="secondary"
                onClick={this.openReallocationModal}
                isLoading={this.state.loadingNearbyDealers}
              >
                <FormattedMessage id="reallocate" defaultMessage="REALLOCATE" />
              </Button>
            </div>
            <div className="OrderDetails__col OrderDetailsLayout__col--centered">
              {this.renderCloseDetailsBtn()}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  renderCloseDetailsBtn = () => {
    const {
      intl: { formatMessage },
    } = this.props;
    const title = formatMessage({
      id: 'close_window',
      defaultMessage: 'Close window',
    });

    return (
      <Tooltip position="left" title={title}>
        <CancelIcon
          role="button"
          onClick={this.props.onCloseDetailsBtnClick}
          className="OrderDetailsLayout__close_btn"
        />
      </Tooltip>
    );
  };

  onOrderDetailsMouseEnter = () => {
    const clipboardButtons = document.querySelectorAll('.clipboard-button');

    this.clipboard = new Clipboard(clipboardButtons);

    this.setState({ isOrderDetailsOptionsOpen: true });
  };

  onOrderDetailsMouseLeave = () => {
    if (this.clipboard) {
      this.clipboard.destroy();
    }

    this.setState({ isOrderDetailsOptionsOpen: false });
  };

  renderOrderDetailsCopyButton = () => {
    // eslint-disable-next-line no-unused-vars
    const { isOrderDetailsOptionsOpen } = this.state;
    const {
      order,
      dealer,
      orderDetails: { serviceArea },
      intl: { formatMessage },
    } = this.props;

    const addressIntl = formatMessage({
      id: 'order_details.customer.address',
      defaultMessage: 'Address',
    });
    const addressExtraIntl = formatMessage({
      id: 'order_details.customer.address_extra',
      defaultMessage: 'Address extra',
    });
    const remarkIntl = formatMessage({
      id: 'order_details.customer.remark',
      defaultMessage: 'Remark',
    });

    const totalIntl = formatMessage({
      id: 'order_details.table.order_total',
      defaultMessage: 'Total',
    });
    const paymentIntl = formatMessage({
      id: 'order_details.table.payment',
      defaultMessage: 'Payment',
    });
    const paymentTypeIntl = type =>
      formatMessage({
        id: `order_details.payment_method.${type}`,
        defaultMessage: order.paymentType,
      });
    const copyBtnTooltip = formatMessage({
      id: 'order_details.copy_order_details',
      defaultMessage: 'Copy order details',
    });
    const orderProducts = order.products.map(
      pr => `${pr.quantity} ${pr.properties.brand} ${pr.properties.type}`,
    );
    const orderProductsString = orderProducts.join('\n');
    const orderTotal = getOrderTotal(order, true);

    const phoneNumber = formatPhone(dealer.phone.replace(/^\+55 */, ''));

    const serviceAreaMessage =
      serviceArea && serviceArea.serviceAreaName
        ? `${formatMessage({ id: 'serviceareas.area-name' })}: ${serviceArea.serviceAreaName}\n`
        : '';

    let orderDetailsText =
      `${dealer.name} (${dealer.id})\n` +
      `${serviceAreaMessage}` +
      `${formatMessage({ id: 'phone_number' })}: ${phoneNumber} \n` +
      `\n` +
      `${formatMessage({ id: 'client' })}: ${order.customerName || '-'}\n` +
      `${addressIntl}: ${order.address || '-'}\n` +
      `${addressExtraIntl}: ${order.addressRemark || '-'}\n` +
      `${remarkIntl}: ${order.customerRemark || '-'} \n` +
      `\n` +
      `${orderProductsString || '-'}\n` +
      `${totalIntl}: R$${orderTotal || '-'}\n` +
      `*${paymentIntl}: ${paymentTypeIntl(order.paymentType)}* `;

    switch (order.paymentType) {
      case CASH: {
        if (order.isChangeNeeded) {
          const rest = order.changeAmount - getOrderTotal(order, true);
          const changeForIntl =
            ` - *${formatMessage({ id: 'change_for' })} ` +
            `${formatPrice(order.changeAmount)} (${formatPrice(rest)})*`;

          orderDetailsText += changeForIntl;
        }
        break;
      }
      case CREDIT:
      case DEBIT: {
        orderDetailsText += ` - *${formatMessage({
          id: 'order_details.payment_method.bring_card_machine',
        })}*`;
        break;
      }
      // eslint-disable-next-line no-empty
      default: {
      }
    }

    return (
      <div key="OrderDetailsCopyButton" className="OrderDetails__copy_details">
        <Tooltip position="left" offset="-60" title={copyBtnTooltip}>
          <button
            type="button"
            className="OrderDetails__copy_details_btn clipboard-button"
            data-clipboard-text={orderDetailsText}
          >
            <CopyIcon className="OrderDetails__copy_details_btn_icon" />
          </button>
        </Tooltip>
      </div>
    );
  };

  renderDealerInfo = () => {
    const {
      dealer,
      intl: { formatMessage },
      orderDetails: { serviceArea },
    } = this.props;

    if (dealer) {
      const { phone } = dealer;
      const phoneNumber = formatPhone(phone.replace(/^\+55 */, ''));

      const phoneClipboardText = phone
        .replace(/^\+55 */, '')
        .replace(/([()])/g, '')
        .replace(/-/g, '');

      return (
        <div className="OrderDetailsLayout__row">
          <IconTextInfo Icon={StoreIcon}>
            <span>
              {dealer.name} <span className="OrderSnippet__dealer_id">({dealer.id})</span>
            </span>

            {serviceArea && serviceArea.serviceAreaName && (
              <React.Fragment>
                <FormattedMessage id="serviceareas.area-name" />: {serviceArea.serviceAreaName}
                <span className="OrderSnippet__dealer_id"> ({serviceArea.serviceAreaId})</span>
              </React.Fragment>
            )}

            <a
              className="OrderDetailsLayout__dealer-link clipboard-button"
              rel="noopener noreferrer"
              data-clipboard-text={phoneClipboardText}
            >
              <Tooltip
                position="left"
                offset="-60"
                title={formatMessage({ id: 'order_details.copy_dealer_phone' })}
              >
                <span>{phoneNumber}</span>
              </Tooltip>
            </a>
          </IconTextInfo>
        </div>
      );
    }

    return null;
  };

  renderCustomerInfo = () => {
    const { order } = this.props;
    const { phone, customerName } = order;
    const phoneNumber = formatPhone(phone.replace(/^\+55 */, ''));
    const name = this.displayText(customerName, 'name');
    const elementWithPhone = (
      <div className="OrderDetailsLayout__row">
        <IconTextInfo Icon={UserIcon}>
          <span>{name}</span>
          <div>{phoneNumber}</div>
        </IconTextInfo>
      </div>
    );

    return elementWithPhone;
  };

  renderAdditionalInfo = () => {
    const { order } = this.props;
    const { address, customerRemark, addressRemark } = order;

    return (
      <div className="OrderDetailsLayout__row">
        <IconTextInfo Icon={PlaceIcon}>
          <div>{address}</div>
          <div>{addressRemark}</div>
          <div>{customerRemark}</div>
        </IconTextInfo>
      </div>
    );
  };

  renderCancelWarningText = () => {
    const {
      order: { status },
    } = this.props;

    if (this.isCancelledByCustomer) {
      return (
        <FormattedMessage id="cancelled_by_customer" defaultMessage="Cancelled by the customer" />
      );
    }

    if (status === ORDER_STATUS_DEALER_DECLINED) {
      return <FormattedMessage id="declined_by_dealer" defaultMessage="Declined by the dealer" />;
    }

    return <FormattedMessage id="cancelled_by_dealer" defaultMessage="Cancelled by the dealer" />;
  };

  unifyCancellation = () => {
    const {
      order: { consumerCancellationInfo, dealerCancellationInfo },
    } = this.props;

    return consumerCancellationInfo || dealerCancellationInfo;
  };

  getSortedEventList = () => {
    const { eventHistory } = this.props;
    const newerFirst = (eventA, eventB) => eventA.creationTime > eventB.creationTime;

    return eventHistory.sort(newerFirst);
  };

  renderCancellationWarning = () => {
    const {
      order: { consumerCancellationInfo, dealerCancellationInfo },
    } = this.props;
    const cancellationInfo = consumerCancellationInfo || dealerCancellationInfo;
    const isCancelled = this.isCancelledByCustomer || this.isCancelledByDealer;

    if (isCancelled) {
      const reasonMsg = cancellationInfo && cancellationReason[cancellationInfo.reason];
      const getCancellationMessage = () => {
        if (reasonMsg) {
          return (
            <FormattedMessage
              id={`cancellation_reason_code_${cancellationInfo.reason}`}
              defaultMessage={reasonMsg}
            />
          );
        }

        return <FormattedMessage id="default_cancellation_reason" defaultMessage="Unknown" />;
      };

      return (
        <div className="OrderDetailsLayout__cancellation_warning">
          <div className="OrderDetailsLayout__row">
            <div className="OrderDetailsLayout__cancellation_warning_icon">
              <WarningIcon className="OrderDetailsLayout__icon OrderDetailsLayout__icon--warning" />
            </div>
            <div className="OrderDetailsLayout__col OrderDetailsLayout__col--centered">
              <div>
                <div>
                  <strong>{this.renderCancelWarningText()}</strong>
                  {cancellationInfo && (
                    <span>
                      <strong>:</strong>
                      &nbsp;
                      <span className="OrderDetailsLayout__cancellation_reason">
                        {getCancellationMessage()}
                      </span>
                    </span>
                  )}
                </div>
                {cancellationInfo && cancellationInfo.message && (
                  <div className="OrderDetailsLayout__cancellation_reason_msg">
                    <div>{`"${cancellationInfo.message}"`}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  renderVoucherContent = () => {
    const {
      order: { voucher },
    } = this.props;

    if (voucher && !this.isCancelNewOrder) {
      return (
        <div className="OrderDetailsLayout__row">
          <div className="OrderDetailsLayout__voucher OrderDetailsLayout__col OrderDetailsLayout__col--centered">
            <IconTextInfo Icon={GiftIcon} iconFullOpacity>
              <strong className="HistoryOrderDetails__voucher_text">
                <FormattedMessage
                  id="order_details_voucher_message"
                  defaultMessage="Discount code used. Chama will pay you back."
                />
              </strong>
            </IconTextInfo>
          </div>
        </div>
      );
    }

    return null;
  };

  renderAcceptedByCustomerService = () => {
    const {
      order: { acceptedByCustomerService },
    } = this.props;

    if (acceptedByCustomerService) {
      return (
        <div className="OrderDetailsLayout__row">
          <div className="OrderDetailsLayout__accepted_by_cs OrderDetailsLayout__col OrderDetailsLayout__col--centered">
            <IconTextInfo Icon={CSIcon} iconFullOpacity>
              <strong className="HistoryOrderDetails__accepted_by_cs_text">
                <FormattedMessage
                  id="orders.cs_processed_message"
                  defaultMessage="This order has been processed by customer service"
                />
              </strong>
            </IconTextInfo>
          </div>
        </div>
      );
    }

    return null;
  };

  renderMap = () => {
    const { order, dealer } = this.props;

    if (dealer) {
      const markers = [
        { lat: order.lat, lng: order.lon },
        { lat: dealer.lat, lng: dealer.lon, type: 'chama' },
      ];

      return (
        <div className="OrderDetailsLayout__map_btn_wrapper">
          <MapThumbBtn orderLat={order.lat} orderLon={order.lon} markers={markers} />
        </div>
      );
    }

    return null;
  };

  renderSwitchLabel = () => {
    const { isAssignedToCurrentUser } = this.props;

    if (!isAssignedToCurrentUser) {
      return (
        <FormattedMessage id="take_over_order" defaultMessage="Take control over this order" />
      );
    }

    return <span />;
  };

  renderFooterContent = () => {
    const {
      order: {
        currentTicket: { queue },
        paymentType,
      },
      isLoadingAssignment,
      isLoadingCommand,
    } = this.props;
    const { isAssignedToCurrentUser, isFetchingEventHistory, isOrderBeingProcessed } = this.props;
    const onOrderEventHistoryModalClick = () => {
      if (isFetchingEventHistory) {
        return false;
      }

      return this.props
        .fetchEventHistory()
        .then(() => this.eventHistoryModal.show())
        .catch(e => {
          throw new Error(e);
        });
    };

    const OrderEventHistoryModalButton = () => {
      const buttonClass = classNames('OrderDetailsLayout__event_history_btn u__normalize_button', {
        'OrderDetailsLayout__event_history_btn--loading': isFetchingEventHistory,
      });

      return (
        <button type="button" className={buttonClass} onClick={onOrderEventHistoryModalClick}>
          <img src={eyeIcon} alt="eye" />
          <strong className="OrderDetailsLayout__event_history_btn_text">
            <FormattedMessage id="see_history" defaultMessage="See history" />
          </strong>
        </button>
      );
    };

    const renderActionButtons = () => {
      if (isAssignedToCurrentUser) {
        const { order } = this.props;
        return (
          <ActionButtons
            isLoadingCommand={isLoadingCommand}
            isLoadingAssignment={isLoadingAssignment}
            startDelivery={this.startDelivery}
            declineOrder={this.declineOrder}
            deliverOrder={this.deliverOrder}
            cancelOrder={this.cancelOrder}
            dismissCancelNewOrder={this.dismissCancelNewOrder}
            dismissCancelDeliverOrder={this.dismissCancelDeliverOrder}
            type={this.ticket.type}
            status={this.ticket.status}
            assignedToUserId={this.ticket.assignedToUserId}
            isAssignedToCurrentUser={isAssignedToCurrentUser}
            isBeingProcessed={isOrderBeingProcessed}
            order={order}
          />
        );
      }

      return <OrderEventHistoryModalButton />;
    };

    if (
      queue !== QUEUE_FINALIZED &&
      !(paymentType === 'inAppPayment' && queue === QUEUE_BEING_DELIVERED)
    ) {
      return (
        <div className="OrderDetailsLayout__action_btns_wrapper">
          <div className="OrderDetailsLayout__switch_wrapper">
            <Switch
              onToggle={this.toggleTicketControl}
              toggled={isAssignedToCurrentUser}
              isLoading={isLoadingAssignment || isOrderBeingProcessed}
              label={this.renderSwitchLabel()}
            />
          </div>
          {renderActionButtons()}
        </div>
      );
    }

    return (
      <div className="OrderDetailsLayout__event_history_btn_wrapper">
        <OrderEventHistoryModalButton />
      </div>
    );
  };

  track = eventName => {
    const {
      order: {
        id: orderId,
        status: orderStatus,
        currentTicket: { id: ticketId },
      },
      currentUser: userId,
    } = this.props;
    const data = { orderId, orderStatus, ticketId, userId };
    AppInsights.trackEvent(eventName, data);
  };

  confirmTakeOver = () => {
    this.props.assignTicketToCurrentUser(this.ticket.id);
  };

  render() {
    const {
      order,
      displayDealerName,
      intl: { formatMessage },
      dealer,
      isAssignedToCurrentUser,
      isFetchingOrderDetails,
      orderDetails,
    } = this.props;
    const { creationTime, delivery } = order;
    const componentClass = classNames('OrderDetailsLayout', {
      'OrderDetailsLayout--incoming': this.ticket.queue === QUEUE_INCOMING,
      'OrderDetailsLayout--being_delivered': this.ticket.queue === QUEUE_BEING_DELIVERED,
      'OrderDetailsLayout--cancelled': this.isCancelledByCustomer || this.isCancelledByDealer,
      'OrderDetailsLayout--chama_controlled':
        this.ticket.queue !== QUEUE_FINALIZED && isAssignedToCurrentUser,
    });
    const logModalTitle = formatMessage({
      id: 'order_event_history',
      defaultMessage: 'Order event history',
    });
    const driverStatusClass = classNames('OrderDetailsLayout__driver_status', {
      'OrderDetailsLayout__driver_status--warn': delivery && delivery.status === 'pending',
      'OrderDetailsLayout__driver_status--danger': delivery && delivery.status === 'rejected',
      'OrderDetailsLayout__driver_status--ok': delivery && delivery.status === 'delivered',
    });

    if (!dealer || isFetchingOrderDetails) {
      return (
        <div className={componentClass} ref={this.handleRef('component')}>
          <div ref={this.handleRef('content')} className="OrderDetailsLayout__content">
            <div className="OrderDetailsLayout__spinner_wrapper">
              <Spinner fill>Carregando</Spinner>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={componentClass} ref={this.handleRef('component')}>
        <div ref={this.handleRef('content')} className="OrderDetailsLayout__content">
          {this.renderHeader()}

          {dealer && (
            <MdtModals
              handleRef={this.handleRef}
              formatMessage={formatMessage}
              nearbyDealersList={this.props.nearbyDealersList}
              dealer={this.props.dealer}
              onTakeOverConfirm={this.confirmTakeOver}
            />
          )}

          <div className="OrderDetailsLayout__panel OrderDetailsLayout__panel--body">
            <div className="OrderDetailsLayout__row">
              <div
                className="OrderDetailsLayout__col OrderDetailsLayout__col--main_info"
                onMouseEnter={this.onOrderDetailsMouseEnter}
                onMouseLeave={this.onOrderDetailsMouseLeave}
              >
                <CSSTransitionGroup
                  transitionName="OrderDetails-copy-button-transition"
                  transitionEnterTimeout={300}
                  transitionLeaveTimeout={200}
                >
                  {this.renderOrderDetailsCopyButton()}
                </CSSTransitionGroup>

                {this.renderCancellationWarning()}
                {this.renderDealerInfo()}
                {this.renderCustomerInfo()}
                {this.renderAdditionalInfo()}
              </div>

              <div className="OrderDetailsLayout__col OrderDetailsLayout__col--map">
                {!displayDealerName && <div>{this.renderCloseDetailsBtn()}</div>}

                {this.renderMap()}
              </div>
            </div>

            {this.renderVoucherContent()}
            {this.renderAcceptedByCustomerService()}
            {this.renderConsumerDeliveredStatus()}

            <div className="OrderDetailsLayout__table_wrapper">
              <OrderPriceOverview order={order} orderDetails={orderDetails} />
            </div>

            <div>
              <div className="OrderDetailsLayout__row OrderDetailsLayout__row--time_action_wrapper">
                <div className="OrderDetailsLayout__col">
                  <div className="OrderDetailsLayout__time_info_wrapper">
                    <div className="OrderDetailsLayout__col">
                      <div className="OrderDetailsLayout__text_line">
                        <TimeTextLine time={creationTime}>
                          <FormattedMessage
                            id="delivery_timeoforder"
                            defaultMessage="Time of ordering"
                          />
                        </TimeTextLine>
                      </div>
                    </div>

                    {delivery && (
                      <React.Fragment>
                        <div className="OrderDetailsLayout__col">
                          <div>
                            <div>
                              <strong>
                                <FormattedMessage
                                  id="order_details.driver"
                                  defaultMessage="Driver:"
                                />
                              </strong>
                            </div>
                            <div>{delivery.driver && delivery.driver.name}</div>
                          </div>
                        </div>

                        <div className="OrderDetailsLayout__col">
                          <div>
                            <div>
                              <strong>Status:</strong>
                            </div>
                            <div className={driverStatusClass}>
                              <FormattedMessage
                                id={`delivery_status.${delivery.status}`}
                                defaultMessage={deliveryStatuses[delivery.status]}
                              />
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    )}

                    <div className="OrderDetailsLayout__col">{this.renderActionTimeContent()}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="OrderDetailsLayout__panel OrderDetailsLayout__panel--footer">
            {this.renderFooterContent()}
          </div>
        </div>

        <Modal
          title={logModalTitle}
          ref={this.handleRef('eventHistoryModal')}
          confirmButtonText="OK"
          hideOnConfirm
        >
          <EventHistory eventList={this.getSortedEventList()} />
        </Modal>
      </div>
    );
  }
}

OrderDetailsLayout.propTypes = propTypes;
OrderDetailsLayout.defaultProps = defaultProps;

const withIntlComponent = injectIntl(windowWatcher(OrderDetailsLayout));

export default withRouter(withIntlComponent);



// WEBPACK FOOTER //
// ./src/modules/orders/components/OrderDetailsLayout/index.jsx